import React, { useState, useEffect, useRef } from 'react';
import { useLayers } from '../../context/LayerContext';
import { useHistory, COMMAND_TYPES } from '../../context/HistoryContext';
import { useContainers } from '../../context/ContainerContext';
import ColorPicker from './ColorPicker';
import { compressImage, formatFileSize, exceedsFileLimit, getRecommendedSettings, getOptimalSettings } from '../../utils/imageCompression';
import './Map.css';
import './LayerLinking.css';

const LayerManager = ({ mapId, areDrawingsVisible, onToggleDrawingsVisibility, onToolChange }) => {

  const uploadInputRef = useRef(null);
  
  // Add history context
  const { pushCommand } = useHistory();
  
  const {
    layers,
    activeLayerId,
    loading,
    error,
    createLayer,
    updateLayer,
    deleteLayer,
    deleteLayers, // eslint-disable-line no-unused-vars
    reorderLayers,
    getActiveLayer,
    createImageLayer,
    setActiveLayerById,

    // Layer grouping functions
    createLayerGroup,
    addLayerToGroup, // eslint-disable-line no-unused-vars
    removeLayerFromGroup,
    removeLayerFromAllGroups,
    deleteLayerGroup,
    updateLayerGroupColor,
    getLayerGroup,
    getGroupedLayers,
    toggleLayerGroupVisibility,
    layerGroups
  } = useLayers();
  
  // Get container context
  const { addContainer } = useContainers();

  // Function to check and set thumbnail if this is the first image
  const checkAndSetThumbnail = async (imageDataUrl, imageName) => {
    // Only proceed if mapId exists and is not 'new'
    if (!mapId || mapId === 'new' || isNaN(parseInt(mapId))) {
      return { thumbnailSet: false, reason: 'invalid_map_id' };
    }

    try {
      // 🔧 Fix: Use correct endpoint for getting game data
      const response = await fetch(`/api/game-states/${mapId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        console.log('Could not check game thumbnail status');
        return { thumbnailSet: false, reason: 'check_failed' };
      }

      const gameData = await response.json();
      
      // 🔧 Fix: More robust thumbnail checking - check if thumbnail exists and is valid
      if (gameData.thumbnail_url && gameData.thumbnail_url.includes('http')) {
        return { thumbnailSet: false, reason: 'already_exists' };
      }

      // Convert data URL to blob for upload
      const responseBlob = await fetch(imageDataUrl);
      const blob = await responseBlob.blob();
      
      // Create form data
      const formData = new FormData();
      formData.append('file', blob, imageName || 'map-image.png');

      // Upload the thumbnail
      const uploadResponse = await fetch(`/api/game-states/${mapId}/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (uploadResponse.ok) {
        const result = await uploadResponse.json();
        
        // ✅ FIXED: Ensure the first image is also tracked as content for proper reference counting
        
        return { thumbnailSet: true, url: result.thumbnail_url };
      } else {
        console.log('Failed to set thumbnail:', uploadResponse.statusText);
        return { thumbnailSet: false, reason: 'upload_failed' };
      }
    } catch (error) {
      console.error('Error setting thumbnail:', error);
      return { thumbnailSet: false, reason: 'error', error };
    }
  };

  const [displayedLayers, setDisplayedLayers] = useState(layers);
  const [draggedLayerId, setDraggedLayerId] = useState(null);
  const [initialLayerCreated, setInitialLayerCreated] = useState(false);
  const [linkingMode, setLinkingMode] = useState(false);
  const [selectedLayersForLinking, setSelectedLayersForLinking] = useState([]);
  // Add confirmation dialog state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [layerToDelete, setLayerToDelete] = useState(null);
  // Add create layer dialog state
  const [showCreateLayerDialog, setShowCreateLayerDialog] = useState(false);
  const [newLayerName, setNewLayerName] = useState('New Layer');
  // Add layer group color picker state
  const [showLayerGroupColorPicker, setShowLayerGroupColorPicker] = useState(false);
  const [layerGroupColorAnchor, setLayerGroupColorAnchor] = useState(null);
  const [selectedLayerGroupForColor, setSelectedLayerGroupForColor] = useState(null);

  useEffect(() => {
    if (!draggedLayerId) {
      setDisplayedLayers(layers);
    }

    // Set initialLayerCreated to true if there are layers
    if (layers.length > 0 && !initialLayerCreated) {
      setInitialLayerCreated(true);
    }
  }, [layers, draggedLayerId, initialLayerCreated]);

  const handleCreateLayer = () => {
    setNewLayerName('New Layer');
    setShowCreateLayerDialog(true);
  };

  const handleConfirmCreateLayer = async () => {
    if (!newLayerName.trim()) return;
    
    // Save current state for undo
    const previousLayers = [...layers];
    const previousActiveLayerId = activeLayerId;
    
    const layerName = newLayerName.trim();
    
    const newLayer = {
      layer_name: layerName,
      layer_data: JSON.stringify({ lines: [] }),
      is_wall_layer: false,
      is_visible: true
    };

    try {
      // Create the layer first and get its full data (including ID)
      const createdLayer = await createLayer(newLayer);

      if (createdLayer && createdLayer.layer_id) {
        // Add to history immediately after creation
        pushCommand({
          type: COMMAND_TYPES.ADD_LAYER,
          targetId: createdLayer.layer_id,
          newState: createdLayer,
          previousLayers: previousLayers,
          newLayers: [...previousLayers, createdLayer],
          previousActiveLayerId: previousActiveLayerId,
          newActiveLayerId: createdLayer.layer_id
        });

        // If this is the second layer, position it lower than the first layer
        if (initialLayerCreated && previousLayers.length === 1) {
          // Reorder ensuring the new layer is first in order array
          const newLayerIds = [createdLayer.layer_id, ...previousLayers.map(l => l.layer_id)];
          reorderLayers(newLayerIds);
        }
      }

      // Close dialog on success
      setShowCreateLayerDialog(false);
    } catch (err) {
      console.error('Error creating layer:', err);
      createBeautifulMessage('Error: Failed to create layer', 'error', 5000);
    }
  };

  const handleCancelCreateLayer = () => {
    setShowCreateLayerDialog(false);
    setNewLayerName('New Layer');
  };

  const handleDeleteLayer = (layerId) => {
    const layerGroup = getLayerGroup(layerId);
    let confirmMessage = 'Are you sure you want to delete this layer?';
    
    if (layerGroup) {
      confirmMessage += `\n\nThis layer is linked to group "${layerGroup.name}". It will be removed from the group.`;
    }
    
    // Store layer info and show custom confirmation dialog
    setLayerToDelete({ 
      id: layerId, 
      group: layerGroup, 
      message: confirmMessage 
    });
    setShowDeleteConfirm(true);
  };

  const handleConfirmDeleteLayer = () => {
    if (!layerToDelete) return;
    
    const { id: layerId, group: layerGroup } = layerToDelete;
    
    // Remove from group first if linked
    if (layerGroup) {
      removeLayerFromGroup(layerId, layerGroup.id);
      // Disband immediately if the group originally had 2 or fewer layers
      if (Array.isArray(layerGroup.layerIds) && layerGroup.layerIds.length <= 2) {
        deleteLayerGroup(layerGroup.id);
      }
    }
    
    // Save current state for undo
    const layerToDeleteData = layers.find(l => l.layer_id === layerId);
    const previousLayers = [...layers];
    const previousActiveLayerId = activeLayerId;
    const previousGroups = layerGroups ? JSON.parse(JSON.stringify(layerGroups)) : [];

    // Derive the groups state after deletion effects (removing the layer from any group and disbanding groups with <2)
    let newGroups = previousGroups.map(g => ({ ...g, layerIds: g.layerIds.filter(id => id !== layerId) }));
    newGroups = newGroups.filter(g => Array.isArray(g.layerIds) && g.layerIds.length >= 2);
    
    // Push command to history before deletion
    pushCommand({
      type: COMMAND_TYPES.DELETE_LAYER,
      targetId: layerId,
      previousState: layerToDeleteData,
      previousLayers: previousLayers,
      previousActiveLayerId: previousActiveLayerId,
      wasLinked: !!layerGroup,
      previousGroup: layerGroup,
      previousLayerGroups: previousGroups,
      newLayerGroups: newGroups
    });
    
    deleteLayer(layerId);
    
    // Close dialog
    setShowDeleteConfirm(false);
    setLayerToDelete(null);
  };

  const handleCancelDeleteLayer = () => {
    setShowDeleteConfirm(false);
    setLayerToDelete(null);
  };

  const handleToggleVisibility = (layerId, isCurrentlyVisible) => {
    const newVisibility = !isCurrentlyVisible;
    const groupedLayerIds = getGroupedLayers(layerId);
    
    // Save current state for undo
    const affectedLayers = layers.filter(l => groupedLayerIds.includes(l.layer_id));
    const previousStates = affectedLayers.map(layer => ({ ...layer }));
    const newStates = affectedLayers.map(layer => ({ ...layer, is_visible: newVisibility }));
    
    // Push command to history
    pushCommand({
      type: COMMAND_TYPES.UPDATE_LAYER,
      targetId: layerId,
      previousState: previousStates.length === 1 ? previousStates[0] : previousStates,
      newState: newStates.length === 1 ? newStates[0] : newStates,
      updateType: 'visibility',
      affectedLayerIds: groupedLayerIds
    });
    
    // Use the new group visibility toggle function
    toggleLayerGroupVisibility(layerId, newVisibility);
  };

  const handleToggleWallLayer = (layerId, isCurrentlyWallLayer) => {
    // Save current state for undo
    const layer = layers.find(l => l.layer_id === layerId);
    const previousState = { ...layer };
    const newState = { ...layer, is_wall_layer: !isCurrentlyWallLayer };
    
    // Push command to history
    pushCommand({
      type: COMMAND_TYPES.UPDATE_LAYER,
      targetId: layerId,
      previousState: previousState,
      newState: newState,
      updateType: 'wall_layer'
    });
    
    updateLayer(layerId, { is_wall_layer: !isCurrentlyWallLayer });
  };

  const handleMoveLayer = (layerId, direction) => {
    const currentLayersArray = [...displayedLayers];
    const currentIndex = currentLayersArray.findIndex(layer => layer.layer_id === layerId);
    if (currentIndex === -1) return;
    
    let targetIndex = currentIndex;
    if (direction === 'up' && currentIndex < currentLayersArray.length - 1) {
      targetIndex = currentIndex + 1;
    } else if (direction === 'down' && currentIndex > 0) {
      targetIndex = currentIndex - 1;
    }
    
    if (targetIndex !== currentIndex) {
      // Save current state for undo
      const previousOrder = displayedLayers.map(layer => layer.layer_id);
      
      [currentLayersArray[currentIndex], currentLayersArray[targetIndex]] = [currentLayersArray[targetIndex], currentLayersArray[currentIndex]];
      const newLayerIds = currentLayersArray.map(layer => layer.layer_id);
      
      // Push command to history
      pushCommand({
        type: COMMAND_TYPES.REORDER_LAYERS,
        targetId: layerId,
        previousOrder: previousOrder,
        newOrder: newLayerIds,
        direction: direction
      });
      
      reorderLayers(newLayerIds);
    }
  };

  const handleUploadClick = () => {
    uploadInputRef.current?.click();
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Save current state for undo
    const previousLayers = [...layers];
    const previousActiveLayerId = activeLayerId;
    const layerName = file.name.replace(/\.[^/.]+$/, "") || "Background Image";
    
    try {
      // Show compression info to user
      const originalSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      
      // Check if compression is needed
      let processedImageData;
      let compressionInfo = '';
      
      if (exceedsFileLimit(file.size, 8) || file.size > 3 * 1024 * 1024) { // Compress if over 8MB limit or 3MB
        
        // Show beautiful compression message with storage info
        const compressionMessage = createBeautifulMessage(
          `Compressing large image (${originalSizeMB} MB) to prevent storage issues, please wait...`,
          'compress'
        );
        
        // First try to load image to get dimensions for optimal settings
        const img = new Image();
        const imageDataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = e => resolve(e.target.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        
        const optimalSettings = await new Promise((resolve, reject) => {
          img.onload = () => {
            // Use optimal settings targeting 6MB to preserve better quality while staying under Cloudinary limit
            const settings = getOptimalSettings(img.width, img.height, 6 * 1024 * 1024);
            resolve(settings);
          };
          img.onerror = () => {
            // Fallback to recommended settings if image load fails
            resolve(getRecommendedSettings(file.size));
          };
          img.src = imageDataUrl;
        });
        
        try {
          const compressionResult = await compressImage(file, optimalSettings);
          processedImageData = compressionResult.dataUrl;
          
          compressionInfo = `Image compressed: ${formatFileSize(compressionResult.originalSize)} → ${formatFileSize(compressionResult.compressedSize)} (${compressionResult.compressionRatio}% reduction)`;
          
          // Clear the compression message
          if (compressionMessage && document.body.contains(compressionMessage)) {
            document.body.removeChild(compressionMessage);
          }
        } catch (compressionError) {
          console.error('Compression failed:', compressionError);
          
          // Clear compression message and show error
          if (compressionMessage && document.body.contains(compressionMessage)) {
            document.body.removeChild(compressionMessage);
          }
          
          createBeautifulMessage('Image compression failed. Using original image...', 'error', 5000);
          
          // Fallback to original file
          const reader = new FileReader();
          reader.onload = (event) => {
            processedImageData = event.target.result;
            processImageData(processedImageData, layerName, previousLayers, previousActiveLayerId, compressionInfo, file.size);
          };
          reader.readAsDataURL(file);
          return;
        }
      } else {
        // File is small enough, use original
        const reader = new FileReader();
        reader.onload = (event) => {
          processedImageData = event.target.result;
          processImageData(processedImageData, layerName, previousLayers, previousActiveLayerId, compressionInfo, file.size);
        };
        reader.readAsDataURL(file);
        return;
      }
      
      // Process the compressed image
      processImageData(processedImageData, layerName, previousLayers, previousActiveLayerId, compressionInfo, file.size);
      
    } catch (err) {
      console.error('Error processing image:', err);
      createBeautifulMessage('Failed to process image. Please try again.', 'error', 5000);
      e.target.value = '';
    }
  };

  // Function to create beautiful styled messages
  const createBeautifulMessage = (message, type = 'info', duration = 4000) => {
    // Add keyframes for animation if not already present
    if (!document.querySelector('#slideInRightKeyframes')) {
      const style = document.createElement('style');
      style.id = 'slideInRightKeyframes';
      style.textContent = `
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `;
      document.head.appendChild(style);
    }

    const messageDiv = document.createElement('div');
    
    // Use the exact same styling as save-load-status-message for consistency
    messageDiv.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
      color: var(--text-color);
      padding: var(--spacing-lg);
      border-radius: 8px;
      border: 2px solid var(--accent-color);
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4), 
                  0 0 20px rgba(160, 44, 44, 0.3);
      z-index: 1000;
      font-family: var(--font-primary);
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      max-width: 300px;
      animation: slideInRight 0.3s ease-out;
      text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
      cursor: pointer;
      transition: opacity 0.2s ease;
    `;
    
    messageDiv.innerHTML = message;
    
    // Add hover to dismiss functionality
    messageDiv.addEventListener('mouseenter', () => {
      messageDiv.style.opacity = '0';
      setTimeout(() => {
        if (document.body.contains(messageDiv)) {
          document.body.removeChild(messageDiv);
        }
      }, 200); // Wait for fade out animation
    });
    
    document.body.appendChild(messageDiv);
    
    // Auto-remove after duration (only if not already removed by hover)
    if (duration > 0) {
      setTimeout(() => {
        if (document.body.contains(messageDiv)) {
          document.body.removeChild(messageDiv);
        }
      }, duration);
    }
    
    return messageDiv;
  };

  // Separate function to handle image processing after compression
  const processImageData = async (imageDataUrl, layerName, previousLayers, previousActiveLayerId, compressionInfo, originalFileSize) => {
    try {
      const img = new window.Image();
      img.src = imageDataUrl;
      
      img.onload = async () => {
        try {
          // Get active layer to determine upload strategy
          const activeLayer = getActiveLayer();
          let targetLayer;
          let operationType;
          // let newLayerCreated = false; // Removed: unused variable
          
          if (activeLayer && activeLayer.is_wall_layer) {
            // If active layer is FOW, find the paired image layer in the same group
            const layerGroup = getLayerGroup(activeLayer.layer_id);
            
            if (layerGroup) {
              // Find the image layer in the same group
              const imageLayerInGroup = layers.find(layer => 
                layerGroup.layers.includes(layer.layer_id) && !layer.is_wall_layer
              );
              
              if (imageLayerInGroup) {
                // Use the existing image layer from the same group
                targetLayer = imageLayerInGroup;
                setActiveLayerById(targetLayer.layer_id);
                operationType = 'ADD_IMAGE_TO_EXISTING_LAYER';
              } else {
                // If no image layer in group, use the first available image layer
                targetLayer = layers.find(layer => !layer.is_wall_layer);
                if (targetLayer) {
                  setActiveLayerById(targetLayer.layer_id);
                  operationType = 'ADD_IMAGE_TO_EXISTING_LAYER';
                }
              }
            } else {
              // No group found, use the first available image layer
              targetLayer = layers.find(layer => !layer.is_wall_layer);
              if (targetLayer) {
                setActiveLayerById(targetLayer.layer_id);
                operationType = 'ADD_IMAGE_TO_EXISTING_LAYER';
              }
            }
            
            // If still no target layer found, something is wrong with the layer setup
            if (!targetLayer) {
              console.error('[LayerManager] No image layer available for upload');
              createBeautifulMessage('Error: No image layer available. Please create a new layer first.', 'error', 5000);
              return;
            }
          } else if (activeLayer && !activeLayer.is_wall_layer) {
            // If active layer is image layer, use it
            targetLayer = activeLayer;
            operationType = 'ADD_IMAGE_TO_EXISTING_LAYER';
          } else {
            // No active layer - this shouldn't happen with default layers, but handle gracefully
            targetLayer = layers.find(layer => !layer.is_wall_layer);
            
            if (!targetLayer) {
              // Fallback: create a new image layer if somehow none exists
              targetLayer = await createImageLayer(layerName);
              operationType = 'ADD_IMAGE_WITH_NEW_LAYERS';
              // newLayerCreated = true; // Removed: unused variable
            } else {
              operationType = 'ADD_IMAGE_TO_EXISTING_LAYER';
            }
          }
          
          if (targetLayer) {
            // Create container instead of direct image
            const newContainer = await addContainer({
              name: layerName,
              layerId: targetLayer.layer_id,
              image: {
                src: imageDataUrl,
                width: img.width,
                height: img.height
              },
              x: 0,
              y: 0,
              scaleX: 1,
              scaleY: 1,
              rotation: 0
            });
            
            if (newContainer) {
              // Check if this is the first image uploaded to the game and set as thumbnail if no thumbnail exists
              const thumbnailResult = await checkAndSetThumbnail(imageDataUrl, layerName);
              
              // Always show success message, with compression info and thumbnail info if available
              let successMessage = 'Image uploaded successfully!';
              if (compressionInfo) {
                successMessage += `<br/><small>${compressionInfo}</small>`;
              }
              if (thumbnailResult.thumbnailSet) {
                successMessage += '<br/><small>Set as game thumbnail!</small>';
              }
              // Add note about large images and undo/redo (check from original size info)
              if (originalFileSize > 5 * 1024 * 1024) { // If original image was > 5MB
                successMessage += '<br/><small>Note: Large images may limit undo/redo history to save memory.</small>';
              }
              
              createBeautifulMessage(successMessage, 'success');
              
              // Add appropriate history command based on operation type
              setTimeout(() => {
                if (operationType === 'ADD_IMAGE_TO_EXISTING_LAYER') {
                  pushCommand({
                    type: COMMAND_TYPES.ADD_IMAGE_TO_EXISTING_LAYER,
                    targetLayerId: targetLayer.layer_id,
                    containerId: newContainer.id,
                    containerData: newContainer, // Store full container data for redo
                    layerName: targetLayer.layer_name,
                    imageName: layerName,
                    previousActiveLayerId: previousActiveLayerId,
                    newActiveLayerId: targetLayer.layer_id
                  });
                } else if (operationType === 'ADD_IMAGE_WITH_NEW_LAYERS') {
                  pushCommand({
                    type: COMMAND_TYPES.ADD_IMAGE_WITH_NEW_LAYERS,
                    targetLayerId: targetLayer.layer_id,
                    fowLayerId: targetLayer.fowLayerId, // Use the stored FOW layer ID
                    containerId: newContainer.id,
                    containerData: newContainer, // Store full container data for redo
                    newLayerState: targetLayer,
                    newFowLayerState: targetLayer.fowLayerData, // Use the stored FOW layer data
                    imageName: layerName,
                    previousLayers: previousLayers,
                    newLayers: [...previousLayers, targetLayer],
                    previousActiveLayerId: previousActiveLayerId,
                    newActiveLayerId: targetLayer.layer_id
                  });
                }
              }, 10); // Small delay to ensure container creation is complete
            }
          }
        } catch (err) {
          console.error('Error creating container from uploaded image:', err);
          
          // Check if this is a localStorage quota error and clean up if needed
          if (err.message && err.message.includes('quota')) {
            console.warn('localStorage quota exceeded during image upload, attempting cleanup');
            if (window.dndMapCleanupHistory) {
              window.dndMapCleanupHistory();
              createBeautifulMessage('Storage full! Cleared history to make space. Please try uploading again.', 'warning', 7000);
            } else {
              createBeautifulMessage('Storage full! Please refresh the page and try again.', 'error', 7000);
            }
          } else {
            createBeautifulMessage('Failed to create image container. Try refreshing the page.', 'error', 5000);
          }
        }
      };
    } catch (err) {
      console.error('Error processing image data:', err);
      
      // Check for storage-related issues
      if (err.message && (err.message.includes('quota') || err.message.includes('storage'))) {
        console.warn('Storage issue during image processing, attempting cleanup');
        if (window.dndMapCleanupHistory) {
          window.dndMapCleanupHistory();
          createBeautifulMessage('Storage full! Cleared history to make space. Please try uploading again.', 'warning', 7000);
        } else {
          createBeautifulMessage('Storage full! Please refresh the page and try again.', 'error', 7000);
        }
      } else {
        createBeautifulMessage('Failed to process image. Please try again.', 'error', 5000);
      }
    }
  };

  // Drag and drop functions
  const handleDragStart = (e, layerId) => {
    setDraggedLayerId(layerId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragEnd = (e) => {
    if (draggedLayerId) {
        // Save current state for undo
        const previousOrder = layers.map(layer => layer.layer_id);
        const newLayerIds = displayedLayers.map(layer => layer.layer_id);
        
        // Only push to history if order actually changed
        if (JSON.stringify(previousOrder) !== JSON.stringify(newLayerIds)) {
          pushCommand({
            type: COMMAND_TYPES.REORDER_LAYERS,
            targetId: draggedLayerId,
            previousOrder: previousOrder,
            newOrder: newLayerIds,
            direction: 'drag'
          });
        }
        
        reorderLayers(newLayerIds);
    }
    setDraggedLayerId(null);
  };

  const handleDragOver = (e, targetLayerId) => {
    e.preventDefault();
    if (!draggedLayerId || draggedLayerId === targetLayerId) return;

    const currentOrder = [...displayedLayers];
    const draggedItemIndex = currentOrder.findIndex(l => l.layer_id === draggedLayerId);
    const targetItemIndex = currentOrder.findIndex(l => l.layer_id === targetLayerId);

    if (draggedItemIndex === -1 || targetItemIndex === -1 || draggedItemIndex === targetItemIndex) return;

    const draggedItem = currentOrder[draggedItemIndex];
    currentOrder.splice(draggedItemIndex, 1);
    currentOrder.splice(targetItemIndex, 0, draggedItem);
    
    setDisplayedLayers(currentOrder);
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
  };

  // Handle layer name editing
  const handleLayerNameEdit = (layerId, newName) => {
    if (newName.trim() && newName.trim() !== '') {
      // Save current state for undo
      const layer = layers.find(l => l.layer_id === layerId);
      
      // Only create history command if name actually changed
      if (layer && layer.layer_name !== newName.trim()) {
        const previousState = { ...layer };
        const newState = { ...layer, layer_name: newName.trim() };
        
        // Push command to history
        pushCommand({
          type: COMMAND_TYPES.UPDATE_LAYER,
          targetId: layerId,
          previousState: previousState,
          newState: newState,
          updateType: 'name'
        });
        
        updateLayer(layerId, { layer_name: newName.trim() });
      }
    }
  };

  // Layer linking functions
  const handleToggleLinking = () => {
    if (linkingMode) {
      // If in linking mode, finalize the current selection
      handleFinalizeLinking();
    } else {
      // Start linking mode
      setLinkingMode(true);
      setSelectedLayersForLinking([]);
    }
  };

  const handleCancelLinking = () => {
    setLinkingMode(false);
    setSelectedLayersForLinking([]);
  };

  const handleLayerSelectForLinking = (layerId) => {
    if (!linkingMode) return;
    
    const layerGroup = getLayerGroup(layerId);
    
    if (layerGroup && selectedLayersForLinking.length === 0) {
      // If clicking on a grouped layer and no selection yet, select all layers in the group
      setSelectedLayersForLinking([...layerGroup.layerIds]);
    } else {
      // Normal toggle behavior
      setSelectedLayersForLinking(prev => {
        if (prev.includes(layerId)) {
          return prev.filter(id => id !== layerId);
        } else {
          return [...prev, layerId];
        }
      });
    }
  };

  const handleChangeLayerGroupColor = (e) => {
    e.preventDefault(); // Prevent default context menu
    
    // Find a layer group from the selected layers for linking
    // OR find the group of any currently selected layer
    let targetGroup = null;
    
    if (selectedLayersForLinking.length > 0) {
      // If in linking mode, find group from selected layers
      for (const layerId of selectedLayersForLinking) {
        const group = getLayerGroup(layerId);
        if (group) {
          targetGroup = group;
          break;
        }
      }
    } else if (activeLayerId) {
      // If not in linking mode, use active layer's group
      targetGroup = getLayerGroup(activeLayerId);
    }
    
    if (!targetGroup) {
      return;
    }
    
    // Ensure anchor element is valid
    const anchorElement = e.currentTarget;
    if (!anchorElement || !anchorElement.isConnected) {
      console.warn('LayerManager ColorPicker: Invalid anchor element');
      return;
    }
    
    setSelectedLayerGroupForColor(targetGroup);
    setLayerGroupColorAnchor(anchorElement);
    setShowLayerGroupColorPicker(true);
  };

  const handleLayerGroupColorChange = (color) => {
    if (!selectedLayerGroupForColor) return;
    
    // Prepare full groups snapshots for robust undo/redo
    const previousGroups = layerGroups ? JSON.parse(JSON.stringify(layerGroups)) : [];
    const newGroups = previousGroups.map(group => (
      group.id === selectedLayerGroupForColor.id
        ? { ...group, color }
        : group
    ));
    
    // Push to history with full groups state
    pushCommand({
      type: COMMAND_TYPES.UPDATE_LAYER_GROUP_COLOR,
      targetId: selectedLayerGroupForColor.id,
      previousGroups,
      newGroups
    });
    
    // Apply the update
    updateLayerGroupColor(selectedLayerGroupForColor.id, color);
    
    // Close the color picker
    setShowLayerGroupColorPicker(false);
    setSelectedLayerGroupForColor(null);
    setLayerGroupColorAnchor(null);
  };

  const handleLayerGroupColorClose = () => {
    // Add a small delay to prevent race conditions
    setTimeout(() => {
      setShowLayerGroupColorPicker(false);
      setSelectedLayerGroupForColor(null);
      setLayerGroupColorAnchor(null);
    }, 10);
  };

  const handleFinalizeLinking = () => {
    if (selectedLayersForLinking.length < 2) {
      // If less than 2 layers selected, check if we need to disband existing groups
      const affectedGroups = new Set();
      
      // Find all groups that contain selected layers
      selectedLayersForLinking.forEach(layerId => {
        const group = getLayerGroup(layerId);
        if (group) {
          affectedGroups.add(group);
        }
      });
      
      // Prepare history snapshots
      const previousGroups = layerGroups ? JSON.parse(JSON.stringify(layerGroups)) : [];
      let newGroups = JSON.parse(JSON.stringify(previousGroups));
      
      // Check each affected group and disband if needed
      affectedGroups.forEach(group => {
        const remainingLayers = group.layerIds.filter(id => !selectedLayersForLinking.includes(id));
        if (remainingLayers.length < 2) {
          // Disband the group
          deleteLayerGroup(group.id);
          // Reflect deletion in newGroups snapshot
          newGroups = newGroups.filter(g => g.id !== group.id);
        }
      });
      
      // If any change occurred, push DELETE_LAYER_GROUP history with full snapshots
      if (JSON.stringify(previousGroups) !== JSON.stringify(newGroups)) {
        pushCommand({
          type: COMMAND_TYPES.DELETE_LAYER_GROUP,
          previousGroups,
          newGroups
        });
      }
      
      // Exit linking mode
      handleCancelLinking();
      return;
    }
    
    // Save current state for undo (deep copy)
    const previousGroups = layerGroups ? JSON.parse(JSON.stringify(layerGroups)) : [];

    // Compute groups after removal (for accurate history and for disbanding singletons)
    const groupsAfterRemoval = previousGroups
      .map(group => ({
        ...group,
        layerIds: group.layerIds.filter(id => !selectedLayersForLinking.includes(id))
      }))
      .filter(group => Array.isArray(group.layerIds) && group.layerIds.length >= 2);

    // Apply removals in state
    selectedLayersForLinking.forEach(layerId => {
      removeLayerFromAllGroups(layerId);
    });
    // Disband any groups that would be left with fewer than 2 layers
    previousGroups.forEach(group => {
      const remaining = group.layerIds.filter(id => !selectedLayersForLinking.includes(id));
      if (remaining.length < 2) {
        deleteLayerGroup(group.id);
      }
    });

    // Create new group without asking for name
    const newGroup = createLayerGroup(selectedLayersForLinking, null);

    // Build newGroups snapshot for redo (after removal + new group)
    const newGroups = [...groupsAfterRemoval, newGroup];

    // Add to history (full groups snapshots)
    pushCommand({
      type: COMMAND_TYPES.CREATE_LAYER_GROUP,
      targetId: newGroup.id,
      newState: newGroup,
      layerIds: selectedLayersForLinking,
      previousGroups,
      newGroups
    });
    
    // Add animation class to newly linked layers
    setTimeout(() => {
      selectedLayersForLinking.forEach(layerId => {
        const layerElement = document.querySelector(`[data-layer-id="${layerId}"]`);
        if (layerElement) {
          layerElement.classList.add('newly-linked');
          setTimeout(() => {
            layerElement.classList.remove('newly-linked');
          }, 600);
        }
      });
    }, 100);
    
    // Exit linking mode
    handleCancelLinking();
  };



  if (loading && !displayedLayers.length) {
    return <div className="layer-manager loading">Loading layers...</div>;
  }

  if (error) {
    return <div className="layer-manager error">{error}</div>;
  }

  const layersToRender = [...displayedLayers].reverse();

  return (
    <div className={`layer-manager ${linkingMode ? 'linking-mode' : ''}`}>
      <div className="layers-title">
        <span>Layers</span>
        {linkingMode && selectedLayersForLinking.length > 0 && (
          <span className="linking-mode-indicator">
            {selectedLayersForLinking.length} selected
          </span>
        )}
      </div>
      
      <div className="layer-actions">
        <button onClick={handleCreateLayer} className="create-layer-btn" title="Create New Layer">
          <i className="fas fa-plus"></i>
        </button>
        <button 
          onClick={() => activeLayerId && handleDeleteLayer(activeLayerId)} 
          className="delete-layer-btn"
          disabled={!activeLayerId || displayedLayers.length <= 1}
          title="Delete Active Layer"
        >
          <i className="fas fa-trash"></i>
        </button>
        <button 
          onClick={handleUploadClick} 
          className="upload-map-btn"
          title="Upload Map Image"
        >
          <i className="fas fa-map"></i>
        </button>
        <button 
          onClick={onToggleDrawingsVisibility} 
          className={`control-button toggle-drawing-visibility-btn ${areDrawingsVisible ? 'active' : ''}`}
          title={areDrawingsVisible ? "Hide All Drawn Lines" : "Show All Drawn Lines"}
        >
          <span className="icon-group">
            {areDrawingsVisible ? <i className="fas fa-eye-slash"></i> : <i className="fas fa-eye"></i>}
            <i className="fas fa-pencil-alt" style={{ marginLeft: '4px' }}></i>
          </span>
        </button>
        
        {/* Layer linking controls */}
        <button 
          onClick={handleToggleLinking}
          onContextMenu={handleChangeLayerGroupColor}
          className={`link-layers-btn ${linkingMode ? 'active' : ''}`}
          title={linkingMode ? "Finish Linking" : "Link Layers Together (Right-click to change group color)"}
          disabled={displayedLayers.length < 2}
        >
          <i className={`fas ${linkingMode ? 'fa-check' : 'fa-link'}`}></i>
        </button>
        <input
          ref={uploadInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileUpload}
        />
      </div>
      
      <ul className="layers-list" onDrop={handleDrop} onDragOver={(e) => e.preventDefault()}> 
        {layersToRender.map((layer) => {
          const actualIndexInDisplayedLayers = displayedLayers.findIndex(l => l.layer_id === layer.layer_id);
          const layerGroup = getLayerGroup(layer.layer_id);
          const isSelectedForLinking = selectedLayersForLinking.includes(layer.layer_id);
          
          return (
            <li 
              key={layer.layer_id}
              data-layer-id={layer.layer_id}
              className={`layer-item 
                ${layer.layer_id === activeLayerId ? 'active' : ''} 
                ${layer.is_wall_layer ? 'wall-layer' : ''} 
                ${draggedLayerId === layer.layer_id ? 'is-dragging' : ''}
                ${layerGroup ? 'linked' : ''}
                ${isSelectedForLinking ? 'selected-for-linking' : ''}`}
              onClick={(e) => {
                if (linkingMode) {
                  e.stopPropagation();
                  handleLayerSelectForLinking(layer.layer_id);
                } else {
                  // Check if switching from FOW layer to non-FOW layer
                  const currentActiveLayer = layers.find(l => l.layer_id === activeLayerId);
                  const newActiveLayer = layer;
                  
                  if (currentActiveLayer?.is_wall_layer && !newActiveLayer.is_wall_layer) {
                    // Switching from FOW to regular layer - change tool to SELECT
                    onToolChange?.('select');
                  }
                  
                  setActiveLayerById(layer.layer_id);
                }
              }}
              draggable={!linkingMode}
              onDragStart={(e) => !linkingMode && handleDragStart(e, layer.layer_id)}
              onDragEnd={!linkingMode ? handleDragEnd : undefined}
              onDragOver={(e) => !linkingMode && handleDragOver(e, layer.layer_id)}
              style={layerGroup ? { borderLeft: `4px solid ${layerGroup.color}` } : {}}
            >
              <span 
                className="layer-visibility"
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleVisibility(layer.layer_id, layer.is_visible);
                }}
                title={layer.is_visible ? "Hide Layer" : "Show Layer"}
              >
                <i className={`fas ${layer.is_visible ? 'fa-eye' : 'fa-eye-slash'}`}></i>
              </span>
              
              <div className="layer-name">
                <span 
                  className="layer-name-text"
                  contentEditable={false}
                  suppressContentEditableWarning={true}
                  title="Click to select layer, double-click to edit name"
                  onBlur={(e) => {
                    e.target.contentEditable = false;
                    handleLayerNameEdit(layer.layer_id, e.target.textContent);
                  }}
                  onClick={(e) => {
                    if (linkingMode) {
                      e.stopPropagation();
                      handleLayerSelectForLinking(layer.layer_id);
                    } else {
                      e.stopPropagation();
                      // Single click - select the layer
                      const currentActiveLayer = layers.find(l => l.layer_id === activeLayerId);
                      const newActiveLayer = layer;
                      
                      if (currentActiveLayer?.is_wall_layer && !newActiveLayer.is_wall_layer) {
                        // Switching from FOW to regular layer - change tool to SELECT
                        onToolChange?.('select');
                      }
                      
                      setActiveLayerById(layer.layer_id);
                    }
                  }}
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    // Double click - enable editing
                    e.target.contentEditable = true;
                    e.target.focus();
                    // Select all text for easy editing
                    const range = document.createRange();
                    range.selectNodeContents(e.target);
                    const selection = window.getSelection();
                    selection.removeAllRanges();
                    selection.addRange(range);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.target.blur();
                    }
                    if (e.key === 'Escape') {
                      e.target.textContent = layer.layer_name;
                      e.target.blur();
                    }
                  }}
                >
                  {layer.layer_name}
                </span>
                {layer.is_wall_layer && <span className="wall-label">🧱</span>}
              </div>
              
              <div className="layer-move">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMoveLayer(layer.layer_id, 'up');
                  }}
                  title="Move Layer Up (Higher)"
                  disabled={actualIndexInDisplayedLayers === displayedLayers.length - 1}
                >
                  <i className="fas fa-arrow-up"></i>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMoveLayer(layer.layer_id, 'down');
                  }}
                  title="Move Layer Down (Lower)"
                  disabled={actualIndexInDisplayedLayers === 0}
                >
                  <i className="fas fa-arrow-down"></i>
                </button>
              </div>
              
              <div className="layer-controls">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleWallLayer(layer.layer_id, layer.is_wall_layer);
                  }}
                  className={`wall-btn ${layer.is_wall_layer ? 'active' : ''}`}
                  title={layer.is_wall_layer ? "Convert to Regular Layer" : "Convert to Wall Layer"}
                >
                  <i className="fas fa-draw-polygon"></i>
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      {/* Create Layer Dialog */}
      {showCreateLayerDialog && (
        <div className="confirmation-overlay">
          <div className="confirmation-dialog">
            <div className="confirmation-header">
              <h3>Create New Layer</h3>
              <button 
                className="close-btn"
                onClick={handleCancelCreateLayer}
              >
                ×
              </button>
            </div>
            <div className="confirmation-body">
              <label htmlFor="layer-name-input">Layer Name:</label>
              <input
                id="layer-name-input"
                type="text"
                value={newLayerName}
                onChange={(e) => setNewLayerName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleConfirmCreateLayer();
                  } else if (e.key === 'Escape') {
                    handleCancelCreateLayer();
                  }
                }}
                autoFocus
                className="layer-name-input"
              />
            </div>
            <div className="confirmation-actions">
              <button 
                className="confirm-btn cancel-btn"
                onClick={handleCancelCreateLayer}
              >
                Cancel
              </button>
              <button 
                className="confirm-btn create-btn"
                onClick={handleConfirmCreateLayer}
                disabled={!newLayerName.trim()}
              >
                Create Layer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="confirmation-overlay">
          <div className="confirmation-dialog">
            <div className="confirmation-header">
              <h3>Delete Layer</h3>
              <button 
                className="close-btn"
                onClick={handleCancelDeleteLayer}
              >
                ×
              </button>
            </div>
            <div className="confirmation-body">
              <p>{layerToDelete?.message.split('\n\n')[0]}</p>
              {layerToDelete?.group && (
                <p className="warning-text">
                  <i className="fas fa-exclamation-triangle"></i>
                  This layer is linked to group "{layerToDelete.group.name}". It will be removed from the group.
                </p>
              )}
            </div>
            <div className="confirmation-actions">
              <button 
                className="cancel-btn"
                onClick={handleCancelDeleteLayer}
              >
                Cancel
              </button>
              <button 
                className="confirm-btn delete-btn"
                onClick={handleConfirmDeleteLayer}
              >
                Delete Layer
              </button>
            </div>
          </div>
        </div>
              )}

      {/* Layer Group Color Picker */}
      <ColorPicker
        isOpen={showLayerGroupColorPicker}
        onClose={handleLayerGroupColorClose}
        initialColor={selectedLayerGroupForColor?.color || '#007bff'}
        onChange={handleLayerGroupColorChange}
        anchorElement={layerGroupColorAnchor}
      />
    </div>
  );
};

export default LayerManager; 